// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for public, indexable, multilingual marketing SEO.
//
// Consumed by FOUR places that must never drift:
//   1. the router            (src/App.tsx — en-at-root + /:lang groups)
//   2. the <Seo> component   (src/metadata/seo.tsx — canonical + hreflang per page)
//   3. the edge middleware   (flashka-web/middleware.ts — crawler <head> injection)
//   4. the sitemap generator (vite.config.mts plugin — sitemap.xml + robots.txt)
//
// IMPORTANT: this module is imported by the Vercel Edge runtime AND by the Vite
// build config (Node). It must stay free of React, the DOM, `import.meta.env`,
// and the `@/` path alias — use RELATIVE imports of PURE data only.
// ─────────────────────────────────────────────────────────────────────────────

import { blogPosts } from '../components/blog/data';
import { helpArticles } from '../components/help/articles';

/** Every UI language the platform ships (en is the fallback / x-default). */
export const SUPPORTED_LANGS = [
  'en',
  'it',
  'es',
  'fr',
  'de',
  'sv',
  'pl',
  'he',
  'ca',
] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** The 8 non-English languages that get a `/<lang>/…` URL prefix. */
export const PATH_LANGS = SUPPORTED_LANGS.filter((l) => l !== 'en') as Exclude<
  Lang,
  'en'
>[];

export const DEFAULT_LANG: Lang = 'en';

/** Right-to-left languages (drive `<html dir>` + `og:locale`). */
export const RTL_LANGS: readonly Lang[] = ['he'];

/** BCP-47-ish OpenGraph locale codes. */
export const OG_LOCALE_MAP: Record<Lang, string> = {
  en: 'en_US',
  it: 'it_IT',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  sv: 'sv_SE',
  pl: 'pl_PL',
  he: 'he_IL',
  ca: 'ca_ES',
};

/**
 * The country each page language stands for (ISO 3166-1 alpha-2): the one its
 * flag shows in the language picker, so English is the UK. The landing's
 * university leaderboard ranks that country's universities (user 2026-09-28).
 */
export const LANG_COUNTRY: Record<Lang, string> = {
  en: 'GB',
  it: 'IT',
  es: 'ES',
  fr: 'FR',
  de: 'DE',
  sv: 'SE',
  pl: 'PL',
  he: 'IL',
  // Catalan's flag is the Senyera, a region's, not a country's; its universities
  // are Spain's (Andorra has one), so the leaderboard ranks Spain's.
  ca: 'ES',
};

/**
 * Canonical production origin. Plain constant (no env) so it resolves
 * identically in the browser, the Node build, and the Edge runtime. Client code
 * that needs an env override imports `SITE_URL` from `@/metadata/site-url`.
 */
export const CANONICAL_ORIGIN = 'https://www.flashka.ai';

/** Default social/OG share image (lives in public/). */
export const DEFAULT_OG_IMAGE = '/social-thumbnail.png';

/** Its pixel size, so a scraper can lay the card out before fetching it. */
export const DEFAULT_OG_IMAGE_SIZE = { width: 1200, height: 630 } as const;

/** The square brand mark (lives in public/) — the Organization logo. Google
 *  wants a logo that reads on a white square; the wide share card does not. */
export const LOGO_IMAGE = '/pwa-512x512.png';
export const LOGO_IMAGE_SIZE = 512;

/** Opt-in structured data for a static route. The landing's and About's graphs
 *  are keyed off their `seoKey` (see `seo/page-content`), not declared here. */
export type JsonLdKind = 'software';

export interface MarketingRoute {
  /** English (root) path, e.g. `/home`. Always starts with `/`, no trailing slash. */
  path: string;
  /** Key under the `seo.` i18n namespace, e.g. `home` → `seo.home.title`. */
  seoKey: string;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  priority: number;
  /** Page-specific structured data to emit (Organization + WebSite are site-wide). */
  jsonLd?: JsonLdKind[];
}

/**
 * The landing's features, each with its own URL — for search, not a page of
 * its own: the URL serves the v3 landing opened on that feature's card, while
 * its head (title, description) and crawler copy name that feature, so Google
 * can list the features under Flashka's result (sitelinks). Ids are the
 * landing's (`landingV3.features.<id>`); every language gets the page under
 * its prefix. The three v2 slugs (flashcard, quizzes, study) are kept for the
 * search history they carry, with their `seo.<seoKey>` copy; the rest have no
 * head copy yet and fall back to the feature card's own (see
 * `seo/page-content` → featurePageCopy).
 */
export const FEATURE_PAGES = [
  { id: 'flashcards', path: '/home/flashcard', seoKey: 'flashcard' },
  { id: 'mindMaps', path: '/home/mind-maps', seoKey: 'mindMaps' },
  { id: 'transcriber', path: '/home/transcriber', seoKey: 'transcriber' },
  { id: 'summaries', path: '/home/notes', seoKey: 'summaries' },
  { id: 'imageOcclusion', path: '/home/image-occlusion', seoKey: 'imageOcclusion' },
  { id: 'examPlanner', path: '/home/exam-planner', seoKey: 'examPlanner' },
  { id: 'spacedRepetition', path: '/home/study', seoKey: 'study' },
  { id: 'quizzes', path: '/home/quizzes', seoKey: 'quizzes' },
  { id: 'oralExam', path: '/home/oral-exam', seoKey: 'oralExam' },
  { id: 'caseStudies', path: '/home/case-studies', seoKey: 'caseStudies' },
] as const;

export type FeaturePage = (typeof FEATURE_PAGES)[number];

/** The feature page an English path (no lang prefix) is, if any. */
export const featurePageForPath = (enPath: string): FeaturePage | undefined => {
  const path = enPath.replace(/\/+$/, '');
  return FEATURE_PAGES.find((f) => f.path === path);
};

/** The feature page whose head reads `seo.<seoKey>`, if any. */
export const featurePageForSeoKey = (seoKey: string): FeaturePage | undefined =>
  FEATURE_PAGES.find((f) => f.seoKey === seoKey);

/**
 * Landing sections that are not feature cards but get a URL of their own the
 * same way — the landing, scrolled to the section (`data-lv3-section="<id>"`).
 * No `seo.<seoKey>` copy yet: the head falls back to the section's own words.
 */
export const SECTION_PAGES = [
  { id: 'agent', path: '/home/ai-agent', seoKey: 'aiAgent' },
] as const;

export type SectionPage = (typeof SECTION_PAGES)[number];

/** Every URL that serves the landing opened somewhere below its top. */
export const LANDING_SUBPAGES: readonly (FeaturePage | SectionPage)[] = [
  ...FEATURE_PAGES,
  ...SECTION_PAGES,
];

/** The section page an English path (no lang prefix) is, if any. */
export const sectionPageForPath = (enPath: string): SectionPage | undefined => {
  const path = enPath.replace(/\/+$/, '');
  return SECTION_PAGES.find((p) => p.path === path);
};

/** The section page whose head reads `seo.<seoKey>`, if any. */
export const sectionPageForSeoKey = (seoKey: string): SectionPage | undefined =>
  SECTION_PAGES.find((p) => p.seoKey === seoKey);

/** Is this English path one of the landing's own URLs (a card or a section)? */
export const isLandingSubpagePath = (enPath: string): boolean =>
  featurePageForPath(enPath) !== undefined || sectionPageForPath(enPath) !== undefined;

/** The v2 pages with no v3 feature of their own: permanently redirected to the
 *  landing (vercel.json, and in-app). */
export const RETIRED_MARKETING_PATHS = ['/home/upload', '/home/highlight'] as const;

/**
 * Static marketing routes. `/` is intentionally NOT listed — it client-redirects
 * to `/home`; resolveMarketingPath('/') maps it onto the `home` entry so the root
 * still gets correct injected meta and a canonical pointing at `/home`.
 */
export const MARKETING_ROUTES: MarketingRoute[] = [
  {
    path: '/home',
    seoKey: 'home',
    changefreq: 'weekly',
    priority: 1.0,
  },
  { path: '/home/about', seoKey: 'about', changefreq: 'monthly', priority: 0.7 },
  ...LANDING_SUBPAGES.map(
    (f): MarketingRoute => ({
      path: f.path,
      seoKey: f.seoKey,
      changefreq: 'monthly',
      priority: 0.8,
      jsonLd: ['software'],
    }),
  ),
  { path: '/blog', seoKey: 'blog', changefreq: 'weekly', priority: 0.7 },
  { path: '/blog/topics', seoKey: 'blogTopics', changefreq: 'monthly', priority: 0.5 },
  {
    path: '/help',
    seoKey: 'help',
    changefreq: 'monthly',
    priority: 0.6,
  },
  { path: '/terms', seoKey: 'terms', changefreq: 'yearly', priority: 0.3 },
  { path: '/legal', seoKey: 'legal', changefreq: 'yearly', priority: 0.3 },
  { path: '/privacy', seoKey: 'privacy', changefreq: 'yearly', priority: 0.3 },
];

/** The relative paths (no leading lang) that the router renders for marketing. */
export const MARKETING_ROUTE_PATHS = MARKETING_ROUTES.map((r) => r.path);

// ── Topic slug helper (mirrors blog-home.tsx) ────────────────────────────────
export const topicSlug = (topic: string): string =>
  encodeURIComponent(topic.toLowerCase().replace(/\s+/g, '-'));

/** Unique blog topics derived from post hashtags. */
export const getBlogTopics = (): string[] => {
  const all = blogPosts.flatMap((p) => p.hashtags);
  return [...new Set(all)];
};

// ── Lang ↔ path helpers ──────────────────────────────────────────────────────

export const isLang = (value: string): value is Lang =>
  (SUPPORTED_LANGS as readonly string[]).includes(value);

export const isPathLang = (value: string): value is Exclude<Lang, 'en'> =>
  value !== 'en' && isLang(value);

/**
 * Split a (possibly lang-prefixed) pathname into its language and the
 * English-equivalent path. `/es/home` → { lang: 'es', path: '/home' };
 * `/home` → { lang: 'en', path: '/home' }.
 */
export const splitLangFromPath = (pathname: string): { lang: Lang; path: string } => {
  const clean = pathname.replace(/\/+$/, '') || '/';
  const segments = clean.split('/').filter(Boolean);
  if (segments.length > 0 && isPathLang(segments[0])) {
    const lang = segments[0] as Lang;
    const rest = '/' + segments.slice(1).join('/');
    return { lang, path: rest === '/' ? '/' : rest };
  }
  return { lang: 'en', path: clean };
};

const LANG_PICK_KEY = 'flashka-lang-pick';

/**
 * A language the visitor picked in a language menu this session. It outranks
 * their stored language on the English root: a signed-in account's language
 * would otherwise bounce a pick of English straight back to `/<lang>/home`.
 */
export const rememberLangPick = (lang: Lang): void => {
  try {
    window.sessionStorage.setItem(LANG_PICK_KEY, lang);
  } catch {
    /* private mode: the pick just lasts until the next redirect */
  }
};

export const pickedLang = (): Lang | null => {
  try {
    const v = window.sessionStorage.getItem(LANG_PICK_KEY);
    return v && isLang(v) ? v : null;
  } catch {
    return null;
  }
};

/** Drop a leading `/<lang>` prefix, returning the English-equivalent path. */
export const stripLangPrefix = (pathname: string): string =>
  splitLangFromPath(pathname).path;

/** Prefix an English path with the language (`en` stays at root). */
export const localizePath = (enPath: string, lang: Lang): string => {
  const normalized = enPath === '' ? '/' : enPath;
  if (lang === 'en') return normalized;
  if (normalized === '/') return `/${lang}`;
  return `/${lang}${normalized}`;
};

/** Absolute canonical URL for an English path in a given language. */
export const canonicalUrl = (enPath: string, lang: Lang): string =>
  `${CANONICAL_ORIGIN}${localizePath(enPath, lang)}`;

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

/** hreflang alternates for all languages + x-default (→ English/root). */
export const buildAlternates = (enPath: string): HreflangAlternate[] => {
  const alternates: HreflangAlternate[] = SUPPORTED_LANGS.map((lang) => ({
    hreflang: lang,
    href: canonicalUrl(enPath, lang),
  }));
  alternates.push({ hreflang: 'x-default', href: canonicalUrl(enPath, 'en') });
  return alternates;
};

// ── Marketing-path resolution ────────────────────────────────────────────────

export interface ResolvedRoute {
  seoKey: string;
  /** English path (no lang prefix), normalized — used for canonical/hreflang. */
  enPath: string;
  params: Record<string, string>;
  route?: MarketingRoute;
}

/**
 * Resolve an English path (no lang prefix) to its SEO descriptor, or null if the
 * path is not a public marketing page. Handles static routes, `/` (→ home), and
 * the three dynamic shapes (`/blog/:postId`, `/blog/topic/:topicId`,
 * `/help/:articleId`).
 */
export const resolveMarketingPath = (enPath: string): ResolvedRoute | null => {
  const path = enPath.replace(/\/+$/, '') || '/';

  // Root → homepage.
  if (path === '/') {
    const route = MARKETING_ROUTES.find((r) => r.path === '/home');
    return { seoKey: 'home', enPath: '/home', params: {}, route };
  }

  const exact = MARKETING_ROUTES.find((r) => r.path === path);
  if (exact)
    return { seoKey: exact.seoKey, enPath: exact.path, params: {}, route: exact };

  // Dynamic: /blog/topic/:topicId (check before /blog/:postId).
  const topicMatch = path.match(/^\/blog\/topic\/([^/]+)$/);
  if (topicMatch) {
    return { seoKey: 'blogTopic', enPath: path, params: { topicId: topicMatch[1] } };
  }

  // Dynamic: /blog/:postId.
  const postMatch = path.match(/^\/blog\/([^/]+)$/);
  if (postMatch && postMatch[1] !== 'topics') {
    return { seoKey: 'blogPost', enPath: path, params: { postId: postMatch[1] } };
  }

  // Dynamic: /help/:articleId.
  const helpMatch = path.match(/^\/help\/([^/]+)$/);
  if (helpMatch) {
    return { seoKey: 'helpArticle', enPath: path, params: { articleId: helpMatch[1] } };
  }

  return null;
};

/** Is the given English path a public marketing page (static or dynamic)? */
export const isMarketingPath = (enPath: string): boolean =>
  resolveMarketingPath(enPath) !== null;

// ── Sitemap enumeration ──────────────────────────────────────────────────────

export interface SitemapEntry {
  /** English path (no lang prefix). */
  path: string;
  changefreq: MarketingRoute['changefreq'];
  priority: number;
  /** ISO date (YYYY-MM-DD) when known. */
  lastmod?: string;
}

/**
 * All indexable English paths: static marketing routes + every blog post +
 * every help article + every blog topic. The sitemap plugin expands each across
 * all languages with `<xhtml:link rel="alternate" hreflang>`.
 */
export const getSitemapEntries = (): SitemapEntry[] => {
  const entries: SitemapEntry[] = MARKETING_ROUTES.map((r) => ({
    path: r.path,
    changefreq: r.changefreq,
    priority: r.priority,
  }));

  for (const post of blogPosts) {
    entries.push({
      path: `/blog/${post.id}`,
      changefreq: 'monthly',
      priority: 0.6,
      lastmod: toIsoDate(post.date),
    });
  }

  for (const article of helpArticles) {
    entries.push({ path: `/help/${article.slug}`, changefreq: 'monthly', priority: 0.5 });
  }

  for (const topic of getBlogTopics()) {
    entries.push({
      path: `/blog/topic/${topicSlug(topic)}`,
      changefreq: 'monthly',
      priority: 0.4,
    });
  }

  return entries;
};

/**
 * Best-effort YYYY-MM-DD from the free-form blog `date` field (e.g.
 * "Mar 11th, 2025"). Strips ordinal suffixes (st/nd/rd/th) that `Date.parse`
 * cannot handle before parsing.
 */
export const toIsoDate = (value: string): string | undefined => {
  const cleaned = value.replace(/(\d{1,2})(st|nd|rd|th)\b/gi, '$1');
  const parsed = new Date(cleaned);
  if (Number.isNaN(parsed.getTime())) return undefined;
  // The date as written, read in local time: `toISOString` would shift it to
  // UTC and name the day before anywhere east of Greenwich.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};
